import { FC, useState, useEffect } from "react";
import Card from "components/common/Card";
import { TickIcon } from "components/common/Icons";
import cross from "assets/icons/close.svg";
import ListModal from "./List.modal";
import SubLimitModal from "./Sublimit.modal";
import { useAppSelector } from "utils/hooks";
import otherBenefitW from "assets/images/otherBenefitW.png";
import waitingW from "assets/images/waitingW.png";
import hospitalW from "assets/images/hospitalW.png";
import otherBenefitM from "assets/images/otherBenefitsM.png";
import waitingM from "assets/images/waitingM.png";
import hospitalM from "assets/images/hospitalM.png";
import excM from "assets/images/excM.png";
import excW from "assets/images/excW.png";
import ZoneModal from "./Zone.model";
import tooltipIcon from "assets/icons/tooltip.svg";
import { Tooltip } from "react-tooltip";
import DOMPurify from "dompurify";
import arrowIcon from "assets/icons/arrow-button.svg";
import VacAndCheckUps from "./Vac&HealthCheckUp.card";
import AddonsImage from "assets/images/addon.svg";

const TableModal = ({ tableData, onClose }: { tableData: string; onClose: () => void }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
      <div className="relative bg-white p-6 rounded-lg min-w-[200px] max-w-[80vw] max-h-[80vh] overflow-auto gap-5">
        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-800" onClick={onClose}>
          <img src={cross} alt="Close" className="w-3 h-3" />
        </button>
        <div className="p-2" dangerouslySetInnerHTML={{ __html: tableData }} />
      </div>
    </div>
  );
};

interface BenefitCardProps {
  id: string;
  cardData: any;
  hasMoratorium: boolean;
  addOns?: any;
}

const screenData: any = {
  hospitalisationBenefits: { name: "Hospitalisation Benefits", borderColor: "border-[#CFE0EC]", bgColor: "bg-[#E5F8FF]", textColor: "text-[#0B7A9F]", tickColor: "#0B7A9F", bg: "#E5F8FF" },
  coverageAndLimits: { name: "Coverage And Limits", borderColor: "border-[#CFE0EC]", bgColor: "bg-[#E5F8FF]", textColor: "text-[#0B7A9F]", tickColor: "#0B7A9F", bg: "#E5F8FF" },
  otherBenefits: { name: "Other Benefits", borderColor: "border-[#D5E7C8]", bgColor: "bg-[#F3FFEA]", textColor: "text-[#78995D]", tickColor: "#78995D", bg: "#F3FFEA" },
  otherBenefitsAndLimits: { name: "Other Benefits & Limits", borderColor: "border-[#D5E7C8]", bgColor: "bg-[#F3FFEA]", textColor: "text-[#78995D]", tickColor: "#78995D", bg: "#F3FFEA" },
  waitingPeriodAndSubLimits: { name: "Waiting Periods", borderColor: "border-[#E3E5C3]", bgColor: "bg-[#FEFFE8]", textColor: "text-[#9EA147]", tickColor: "#9EA147", bg: "#FEFFE8" },
  waitingPeriodAndSubLimitsV2: { name: "Waiting Periods", borderColor: "border-[#E3E5C3]", bgColor: "bg-[#FEFFE8]", textColor: "text-[#9EA147]", tickColor: "#9EA147", bg: "#FEFFE8" },
  exclusions: { name: "Exclusions", borderColor: "border-[#E7CBCA]", bgColor: "bg-[#FFEFEE]", textColor: "text-[#C67C78]", tickColor: "#C67C78" }
};

const BenefitCard: FC<BenefitCardProps> = ({ id, cardData, hasMoratorium, addOns = "" }) => {
  const [showModals, setShowModal] = useState<{ show: boolean; key: string }>({ show: false, key: "" });
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [showTableModal, setShowTableModal] = useState<{ show: boolean; key: string; }>({ show: false, key: "" });

  const policyData = useAppSelector((state) => state.rootReducer.kypSlice.policyData);
  const isPaymentDone = ["PAID", "WAIVEOFF"].includes(policyData?.kypPaymentStatus);
  const htmlTableKeys = ["adultVaccination", "maternityBenefits", "paCoverage", "healthCheckUp", "wellnessProgram", "rehabAndPainList", "childVaccination", "cataractCoverage", "zoneAndCopayment", "newBornBabyList"];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getWebImage = () => {
    switch (id) {
      case "otherBenefits":
      case "otherBenefitsAndLimits":
        return otherBenefitW;
      case "waitingPeriodAndSubLimits":
        return waitingW;
      case "hospitalisationBenefits":
      case "coverageAndLimitsV2":
        return hospitalW;
      default:
        return excW;
    }
  };
  const getMobileImage = () => {
    switch (id) {
      case "otherBenefits":
      case "otherBenefitsAndLimits":
        return otherBenefitM;
      case "waitingPeriodAndSubLimits":
        return waitingM;
      case "hospitalisationBenefits":
      case "coverageAndLimitsV2":
        return hospitalM;
      default:
        return excM;
    }
  };

  if (!isPaymentDone) {
    return (
      <div className="w-full relative">
        <img src={isMobile ? getMobileImage() : getWebImage()} className="lg:my-10 my-5 w-full object-cover" />
      </div>
    );
  }

  const openModelHandler = (key: string) => {
    if (key.includes(":modern-treatment-sublimits")) return "Sub-Limits (Modern Treatment Coverage)";
    else if (key.includes(":newBornBabyList")) return "Newborn Coverage";
    else if (key.includes(":maternityBenefits")) return "Maternity Benefits";
    else if (key.includes(":wellnessProgram")) return "Wellness Program";
    else if (key.includes(":rehabAndPainList")) return "Rehab & Pain Coverage";
    else if (key.includes(":cataractCoverage")) return "Cataract Coverage";
    else if (key.includes(":waiting-period-sublimits")) return "Sub-Limits Treatment";
    else if (key.includes(":zoneAndCopayment")) return "Zone And Co-Payment";
    else if (key.includes(":paCoverage")) return "Personal Accident Cover";
    else if (key.includes("advancedLists")) return "Modern Treatment Coverage";
    else if (key.includes("treatment")) return "Sub-Limits Treatment";
    else if (key.includes(":zone")) return "Sub Limits for Cataract Treatment";
    else if (key.includes("sub-limits")) return "Sub-Limits (Modern Treatment Coverage)";
    else if (key.includes("list of diseases")) return "List of Named Ailments";
    else if (key.includes("list of ailments")) return "List of Named Ailments";
    else if (key.includes("Modern Treatment Coverage")) return "Modern Treatment Coverage";
    else if (key.includes("healthCheckUp")) return "Health Checkup data";
    else if (key.includes("adultVaccination")) return "Adult Vaccination";
    else if (key.includes("childVaccination")) return "Child Vaccination";

    return false;
  };

  const showModalDataHandler = (key: string) => {
    if (key.includes(":modern-treatment-sublimits")) return cardData?.["MODERN_TREATMENT_SUB_LIMITS"];
    else if (key.includes(":newBornBabyList")) return cardData?.NEW_BORN_BABY_COVERAGE_LIST;
    else if (key.includes(":maternityBenefits")) return cardData?.MATERNITY_BENEFITS_TABLE;
    else if (key.includes(":wellnessProgram")) return cardData?.WELLNESS_PROGRAM_TABLE;
    else if (key.includes(":rehabAndPainList")) return cardData?.REHAB_AND_PAIN_COVERAGE_LIST;
    else if (key.includes(":cataractCoverage")) return cardData?.CATARACT_COVERAGE_LIST;
    else if (key.includes(":waiting-period-sublimits")) return cardData?.["SUB_LIMITS_LIST_TREATMENT"]?.["sublimits"];
    else if (key.includes(":zoneAndCopayment")) return cardData?.ZONE_AND_COPAYMENT_LIST;
    else if (key.includes(":paCoverage")) return cardData?.PERSONAL_ACCIDENT_COVER_LIST;
    else if (key.includes("advancedLists")) return cardData?.["MODERN_TREATMENT_COVERAGE_LIST"];
    else if (key.includes("treatment")) return cardData?.["SUB_LIMITS_LIST_TREATMENT"]?.["sublimits"];
    else if (key.includes(":zone")) return cardData?.["SUB_LIMITS_LIST_ZONE"];
    else if (key.includes("sub-limits")) return cardData?.["MODERN_TREATMENT_SUB_LIMITS"];
    else if (key.includes("list of diseases")) return cardData?.["NAMED_AILMENT_LIST"];
    else if (key.includes("list of ailments")) return cardData?.["NAMED_AILMENT_LIST"];
    else if (key.includes("healthCheckUp")) return cardData?.["HEALTH_CHECK_UP_DETAILS"];
    else if (key.includes("adultVaccination")) return cardData?.["ADULT_VACCINATION_DETAILS"];
    else if (key.includes("childVaccination")) return cardData?.["CHILD_VACCINATION_DETAILS"];
    else if (key.includes("Modern Treatment Coverage") && cardData?.["MODERN_TREATMENT_COVERAGE_LIST"]) return cardData?.["MODERN_TREATMENT_COVERAGE_LIST"];
    else if (key.includes("Modern Treatment Coverage") && cardData?.["MODERN_TREATMENT_SUB_LIMITS"]) return cardData?.["MODERN_TREATMENT_SUB_LIMITS"];

    return false;
  };

  const htmlHandler = (html: string, bg: string): string => {
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "span", "div", "p", "button", "li", "ul"],
      ALLOWED_ATTR: ["href", "style"]
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedHtml, "text/html");

    const buttons = doc.querySelectorAll("button");

    buttons.forEach((button) => {
      let text = button.textContent?.trim() || "";

      // Remove everything after colon
      text = text.split(":")[0].trim();

      const styledSpan = doc.createElement("span");
      styledSpan.setAttribute(
        "style",
        `
      background-color: ${bg};
      display: inline-flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 13px;
      padding-inline: 10px;
      border: 1px solid #DEDFB7;
      border-radius: 4px;
    `
      );
      styledSpan.innerHTML = `
      ${text}
      <img src="${arrowIcon}" style="width:14px; height:14px;" />
    `;

      button.replaceWith(styledSpan);
    });

    return doc.body.innerHTML;
  };

  return (
    <>
      {/* Web View - Only visible on desktop */}
      <Card parentClassName="my-10 relative w-full hidden md:flex" className="w-full">
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-6 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        <div className={`absolute -top-5 -bottom-5 -left-3 ${id === "exclusions" ? "w-[8%]" : "w-[27%]"} border-[5px] rounded-[15px] ${screenData[id].borderColor} ${screenData[id].bgColor}`}></div>

        {/* Render content for benefits cards */}
        {["hospitalisationBenefits", "otherBenefits", "otherBenefitsAndLimits", "waitingPeriodAndSubLimits", "waitingPeriodAndSubLimitsV2", "coverageAndLimits"].includes(id) &&
          Object.values(cardData || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number, arr: any[]) => {
              const isDisclaimer = ["waitingPeriodAndSubLimits", "waitingPeriodAndSubLimitsV2"].includes(id) && Boolean(obj?.disclaimer);
              const isTableData = Boolean(obj?.table_data);
              const disclaimer = obj?.disclaimer;
              return (
                <div key={i} className={`relative items-center w-full ${i !== arr.length - 1 ? "border-b-2 border-[#0000001A]" : ""} ${obj?.key ? "flex" : "hidden"} -ml-2`}>
                  <div className="flex gap-3 text-sm md:text-base font-extrabold items-center p-5 w-[27%]" onClick={() => setShowModal({ show: true, key: obj.key })}>
                    <p className="w-4">
                      <TickIcon color={screenData[id].tickColor} />
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(obj?.key) }} />
                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}{" "}
                    <Tooltip
                      delayShow={1}
                      key={i}
                      id={obj?.tooltip}
                      place="right"
                      className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg"
                      render={() => (
                        <div className="text-white text-sm font-medium ">{obj?.tooltip}</div> // Ensure normal font weight
                      )}
                    />
                  </div>
                  <div className={`w-[70%] ${isDisclaimer ? "pt-5 pl-5 pr-5" : "p-5"}`}>
                    <div className="flex flex-wrap gap-1">
                      <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => (isTableData ? setShowTableModal({ show: true, key: obj?.table_data}) : setShowModal({ show: true, key: obj.value }))} />
                    </div>
                    {showTableModal.show && <TableModal tableData={showTableModal.key} onClose={() => setShowTableModal({ show: false, key: ""})} />}
                    {isDisclaimer && (
                      <div className=" mt-3 font-semibold text-[13px] px-1 rounded-t-[4px] bottom-0 border-l-[5px] border-b-0 border-[#0000002A] p-1" style={{ boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.3)" }}>
                        {disclaimer}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

        {["coverageAndLimits"].includes(id) &&
          Object.values(addOns || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number, arr: any[]) => {
              return (
                <div key={i} className={`relative items-center w-full ${i === 0 && "border-t-2"} ${i !== arr.length - 1 ? "border-b-2  border-[#0000001A]" : ""} ${obj?.key ? "flex" : "hidden"} -ml-2`}>
                  <div className="flex gap-3 text-sm md:text-base font-extrabold items-center p-5 w-[27%]" onClick={() => setShowModal({ show: true, key: obj.key })}>
                    <div className="space-y-1">
                      <img src={AddonsImage} />
                      <div className="flex gap-3 text-sm items-center">
                        <p className="w-4">
                          <TickIcon color={screenData[id].tickColor} />
                        </p>
                        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(obj?.key) }} />
                      </div>
                    </div>
                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}{" "}
                    <Tooltip
                      delayShow={1}
                      key={i}
                      id={obj?.tooltip}
                      place="right"
                      className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg"
                      render={() => (
                        <div className="text-white text-sm font-medium ">{obj?.tooltip}</div> // Ensure normal font weight
                      )}
                    />
                  </div>

                  <div className={`w-[70%] p-5 `}>
                    <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => setShowModal({ show: true, key: obj?.value })} />
                  </div>
                </div>
              );
            })}

        {/* Render content for exclusions */}
        {id === "exclusions" &&
          cardData &&
          cardData.map((data: any, i: number) => (
            <div key={i} className={`relative flex justify-center items-center w-full ${i !== cardData.length - 1 ? "border-b-2 border-[#0000001A]" : ""} -ml-2`}>
              <div className="flex items-center justify-center h-full w-[8%] -ml-2">
                <img src={cross} className="object-contain" alt="Excluded" />
              </div>

              <div className="w-[92%] p-5">
                <p className="font-semibold text-gray-800" dangerouslySetInnerHTML={{ __html: data }}></p>
              </div>
            </div>
          ))}
      </Card>
      {/* Mobile View */}
      <Card className={`w-full ${screenData[id].borderColor} border-2 rounded-[10px]`} parentClassName={`relative md:hidden ${hasMoratorium ? "mb-5" : "mb-10"}  mt-6 rounded-[15px] flex`}>
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-5 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        {/* Render content for benefits cards on mobile */}
        {["hospitalisationBenefits", "otherBenefits", "otherBenefitsAndLimits", "waitingPeriodAndSubLimits", "waitingPeriodAndSubLimitsV2", "coverageAndLimits", "otherBenefitsAndLimits"].includes(id) &&
          Object.values(cardData || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number) => {
              const isDisclaimer = ["waitingPeriodAndSubLimits", "waitingPeriodAndSubLimitsV2"].includes(id) && Boolean(obj?.disclaimer);
              const disclaimer = obj?.disclaimer;
              const isTableData = obj?.table_data;
              return obj?.key ? (
                <div key={i} className={`block w-full`}>
                  <div className={`flex gap-3 font-extrabold items-center w-full px-5 py-3 ${screenData[id].bgColor}`} onClick={() => setShowModal({ show: true, key: obj.key })}>
                    <p className="w-4">
                      <TickIcon color={screenData[id].tickColor} />
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: obj?.key }} />
                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}
                    <Tooltip delayShow={1} key={i} id={obj?.tooltip} place="bottom" className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg" render={() => <div className="text-white text-sm font-medium ">{obj?.tooltip}</div>} />
                  </div>

                  <div className={`w-full px-5 ${isDisclaimer ? "pt-3" : "py-3"} text-sm`}>
                    <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => (isTableData ? setShowTableModal({ show: true, key: obj?.table_data }) : setShowModal({ show: true, key: obj.value }))} />
                    {showTableModal.show && <TableModal tableData={showTableModal.key} onClose={() => setShowTableModal({ show: false, key: ""})} />}
                    {isDisclaimer && (
                      <div className=" mt-3 font-semibold text-xs px-1 rounded-t-[4px] border-l-[5px] border-[#0000002A] bg-white p-1" style={{ boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.3)" }}>
                        {disclaimer}
                      </div>
                    )}
                  </div>
                </div>
              ) : null;
            })}

        {["coverageAndLimits"].includes(id) &&
          Object.values(addOns || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number) => {
              return obj?.key ? (
                <div key={i} className={`block w-full`}>
                  <div className={`flex gap-3 font-extrabold items-center w-full px-5 py-3 ${screenData[id].bgColor}`} onClick={() => setShowModal({ show: true, key: obj.key })}>
                    <p className="w-4">
                      <TickIcon color={screenData[id].tickColor} />
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: obj?.key }} />
                    <img src={AddonsImage} />

                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}
                    <Tooltip delayShow={1} key={i} id={obj?.tooltip} place="bottom" className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg" render={() => <div className="text-white text-sm font-medium ">{obj?.tooltip}</div>} />
                  </div>

                  <div className={`w-full px-5 py-3 text-sm`}>
                    <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => setShowModal({ show: true, key: obj?.value })} />
                  </div>
                </div>
              ) : null;
            })}

        {/* Render content for exclusions on mobile */}
        {id === "exclusions" &&
          cardData &&
          cardData.map((data: any, i: number) => (
            <div key={i} className="flex w-full">
              <div className={`${screenData[id].bgColor} flex gap-3 font-extrabold items-center w-12 justify-center`}>
                <img src={cross} alt="Excluded" />
              </div>
              <div className="w-[92%] p-3">
                <p className="font-semibold text-gray-800" dangerouslySetInnerHTML={{ __html: data }}></p>
              </div>
            </div>
          ))}
      </Card>
      {/* Modals - Only shown when needed */}
      {isPaymentDone && (
        <>
          {showModals?.key && (showModals?.key.includes("<button>") || showModals?.key.includes("<link>")) && !showModals?.key.includes("sub-limits") && !showModals?.key.includes("paCoverage") && !showModals?.key.includes(":zone") && !(showModals?.key.includes("<u><b>Modern Treatment Coverage</b></u>") && !cardData?.MODERN_TREATMENT_COVERAGE_LIST) && htmlTableKeys.some((word) => !showModals?.key?.includes(word)) && <ListModal isOpen={showModals?.show && openModelHandler(showModals?.key)} heading={openModelHandler(showModals?.key)} onClose={() => setShowModal({ show: false, key: "" })} content={showModalDataHandler(showModals?.key)} />}
          {showModals?.key && (showModals?.key.includes("<button>") || showModals?.key.includes("<link>")) && (showModals?.key.includes("modern-treatment-sublimits") || showModals?.key.includes("sub-limits") || (showModals?.key.includes("<u><b>Modern Treatment Coverage</b></u>") && !cardData?.MODERN_TREATMENT_COVERAGE_LIST && cardData?.["MODERN_TREATMENT_SUB_LIMITS"])) && <SubLimitModal isOpen={showModals.show && openModelHandler(showModals.key)} heading={openModelHandler(showModals.key)} onClose={() => setShowModal({ show: false, key: "" })} content={showModalDataHandler(showModals?.key)} />}
          {showModals?.key && (showModals?.key.includes("<button>") || showModals?.key.includes("<link>")) && showModals?.key.includes(":zone") && !showModals?.key.includes(":zoneAndCopayment") && <ZoneModal isOpen={showModals?.show && openModelHandler(showModals?.key)} heading={openModelHandler(showModals?.key)} onClose={() => setShowModal({ show: false, key: "" })} content={showModalDataHandler(showModals.key)} />}
          {htmlTableKeys.some((word) => showModals?.key?.includes(word)) && <VacAndCheckUps isOpen={showModals?.show && openModelHandler(showModals?.key)} heading={openModelHandler(showModals?.key)} onClose={() => setShowModal({ show: false, key: "" })} content={showModalDataHandler(showModals.key)} />}
        </>
      )}
    </>
  );
};

export default BenefitCard;
